import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';

import { NetworkRecommendation } from '../NetworkRecommendation';
import { RevealProgressBar } from './RevealProgressBar';
import { determiningProgress } from '../../lib/revealProgress';
import type {
  NetworkApplyDecisions,
  NetworkUserConstraints,
  SourceFeedRecommendation,
} from '../../lib/networkRecommendation';
import {
  candidateLabel,
  phaseIndex,
  securedBridgeOffers,
  type RevealCandidate,
} from '../../lib/revealStaging';
import type { SourceAnalysisState } from '../../lib/useSourceAnalysis';

// How long the determining beat holds its forward control before offering an
// escape. The analysis recompute is local + usually fast; if it drags (e.g. the
// boat dropped mid-analysis) the user must not be stranded behind the gate.
const DETERMINE_WATCHDOG_MS = 45_000;

// The DECISION beat of the merged Gateways stage (reveal-gateways-onetab): the
// primary-gateway pick, rendered IN the gateway stage (testid `reveal-stage-found`)
// rather than on a separate "Connections" stage. It mounts the shared
// <NetworkRecommendation>, which delivers the confidence-calibrated framing (its
// headline keys off rec.shape: settled for clean/single, "pick your main one" for
// multi) and the per-gateway rationale + alternatives + override (gatewayExplain
// behind each card's "Why?") + the on/off + make-primary edit controls.
//
// Surrounding states: an honest "analyzing your network" beat while the pick is
// in flight (task 7.1, no fake spinner) — a STEP-BASED progress bar mapping the
// coarse real signals (analysis lane, device arrival, snapshot phase) to the
// determining steps (lib/revealProgress.ts), with the step label as the stage text
// under the bar. NO forward control while determining, so advancing is held until
// the pick lands (D2); a one-tap kick + escape (and no bar) when analysis can't run
// ('none' offline). A hard analysis failure is surfaced by the page as a blocking
// screen (task 7.5), not here.

export interface RevealConnectProps {
  analysis: SourceAnalysisState;
  /** Discovered gateways — shown as "weighing these" while analysis settles. */
  candidates: RevealCandidate[];
  /** Live device count — surfaced as concurrent background progress (D14). */
  deviceCount: number;
  /** Persist the confirmed gateway plan (source-analysis apply relay). */
  onApply: (decisions: NetworkApplyDecisions) => Promise<void>;
  /**
   * Re-run the AI against the user's edits as HARD constraints (source-analysis
   * reevaluate relay) — an edited draft swaps the pick's "Apply changes" CTA for
   * "Reevaluate", and the reevaluated rec remounts the recommendation.
   */
  onReevaluate?: (constraints: NetworkUserConstraints) => Promise<void>;
  /** True while a submitted reevaluate is in flight — locks the pick controls. */
  reevaluating?: boolean;
  /** Kick a re-analysis from retained candidates (setup_reanalyze verb). */
  onTriggerAnalysis: () => void;
  /** Can we trigger analysis right now (boat online, session known)? */
  canTriggerAnalysis: boolean;
  /** Current setup phase — advances the step-based determining bar. */
  phase: string | null;
  /** Advance to the next stage (apply done, or "skip for now"). */
  onContinue: () => void;
}

export function RevealConnect({
  analysis,
  candidates,
  deviceCount,
  onApply,
  onReevaluate,
  reevaluating,
  onTriggerAnalysis,
  canTriggerAnalysis,
  phase,
  onContinue,
}: RevealConnectProps): JSX.Element {
  // High-water mark for the step-based bar, so a momentarily-regressed signal
  // never moves the bar backward.
  const maxStepRef = useRef(0);
  const [timedOut, setTimedOut] = useState(false);

  // The analysis is genuinely IN FLIGHT while 'loading', or while 'none' AND it can
  // still run — `useSourceAnalysis` reports a 404 as 'none' for the ENTIRE compute
  // (it flips to 'ready' only on the WS notify), so the real determining wait lives
  // in 'none', not the sub-second 'loading'. `deadEnd` is 'none' that CANNOT run
  // (offline / no session) — a real escape case, not "determining".
  const inFlight = analysis.kind === 'loading' || (analysis.kind === 'none' && canTriggerAnalysis);
  const deadEnd = analysis.kind === 'none' && !canTriggerAnalysis;

  // NOTE: the initial analysis kick lives in the parent (Reveal.tsx scan-settle
  // effect), which fires under the same conditions this beat is shown (settled +
  // candidates + can-trigger). A second mount-kick here used to double-fire — two
  // `setup_reanalyze` POSTs, two requestIds, two billed LLM runs — so it was
  // removed. The "Map my network" retry button still calls onTriggerAnalysis.

  // Watchdog: hold the gate while determining, but reveal an escape if it drags so
  // the user is never stranded behind it. Reset whenever we leave the in-flight state.
  useEffect(() => {
    if (!inFlight) {
      setTimedOut(false);
      return;
    }
    const id = setTimeout(() => setTimedOut(true), DETERMINE_WATCHDOG_MS);
    return () => clearTimeout(id);
  }, [inFlight]);

  // bridge_ftue_offer: secured-bridge candidates (signalk_n2k_bridge +
  // requiresCredentials, core never auto-creates them) present in this round's
  // discovered candidates — rendered as the picker's opt-in offer row.
  const bridgeOffers = useMemo(() => securedBridgeOffers(candidates), [candidates]);

  if (analysis.kind === 'ready') {
    const rec: SourceFeedRecommendation = analysis.recommendation;
    return (
      <div className="reveal-stage reveal-connect" data-testid="reveal-stage-found">
        <NetworkRecommendation
          // Remount on a new rec (a reevaluate lands a new requestId) so the draft
          // re-seeds from the reevaluated suggestions → un-edited again.
          key={rec.requestId}
          recommendation={rec}
          onApply={onApply}
          onReevaluate={onReevaluate}
          reevaluating={reevaluating}
          onSkip={onContinue}
          onDone={onContinue}
          bridgeOffers={bridgeOffers}
        />
      </div>
    );
  }

  // loading / none: honest, never a fake spinner. The step-based bar maps coarse
  // real signals to the determining steps (its label is the stage text under the
  // bar) and CONTINUES the scan bar (no backward jump).
  const prog = determiningProgress(
    { analysis: analysis.kind, deviceCount, phaseIndex: phaseIndex(phase) },
    maxStepRef.current
  );
  maxStepRef.current = prog.currentStep;
  // Honor D2: while the pick is actively being determined show ONLY the bar — no
  // forward control. The escape (Skip, and a retry) appears ONLY when the analysis
  // genuinely can't run (offline) or after the watchdog, so the user is never both
  // told "analyzing…" and offered to re-run/skip it at the same time.
  const showBar = inFlight;
  const showEscape = deadEnd || (inFlight && timedOut);
  const showRetry = timedOut && canTriggerAnalysis;

  return (
    <div className="reveal-stage reveal-connect" data-testid="reveal-stage-found">
      <h3 className="reveal-stage__title">Mapping how your boat connects</h3>
      {showBar ? (
        <RevealProgressBar
          value={prog.fraction}
          label="Analyzing your network"
          valueText={`Step ${prog.currentStep} of ${prog.totalSteps}`}
          description={prog.label}
          testId="reveal-determining-progress"
        />
      ) : null}
      {deadEnd ? (
        <p className="reveal-stage__sub" aria-live="polite">
          We’ll choose your main connection once your boat is back online — or skip for now.
        </p>
      ) : null}
      {inFlight && timedOut ? (
        <p className="reveal-stage__sub" aria-live="polite">
          This is taking longer than expected — you can keep waiting, or skip for now.
        </p>
      ) : null}
      {/* Fill the wait (design D11): show the discovered gateways being weighed
          rather than an abstract spinner alone — visually continuous with the
          found stage and previewing the role board about to appear. */}
      {candidates.length > 0 ? (
        <ul className="reveal-weighing" data-testid="reveal-weighing">
          {candidates.map((c, i) => (
            <li className="reveal-weighing__item" key={c.id ?? `${candidateLabel(c)}-${i}`}>
              {candidateLabel(c)}
            </li>
          ))}
        </ul>
      ) : null}
      {/* Concurrent device-finding progress (design D14): devices keep arriving
          while the decision settles, so this counter is honestly live here. */}
      <p
        className="reveal-stage__sub reveal-connect__devicecount"
        aria-live="polite"
        data-testid="reveal-connect-devicecount"
      >
        {deviceCount > 0
          ? `Meanwhile, cataloging what's aboard… ${deviceCount} found.`
          : "Meanwhile, cataloging what's aboard…"}
      </p>
      {/* The forward control is GATED while determining (reveal-gateways-onetab D2):
          the escape appears ONLY when the analysis can't run (offline) or the
          watchdog fired — never alongside an actively-climbing bar. */}
      {showEscape ? (
        <div className="reveal-actions">
          {showRetry ? (
            <button type="button" className="btn btn-outline" onClick={onTriggerAnalysis}>
              Map my network
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-primary"
            onClick={onContinue}
            data-testid="reveal-gateways-skip"
          >
            Skip for now
          </button>
        </div>
      ) : null}
    </div>
  );
}
